import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { finalize, map, Observable } from 'rxjs';
import { ToastrService } from 'ngx-toastr';

import { ContractorRegistration, Document } from 'src/app/models';
import { Contractor } from 'src/app/models/contractor';
import { ContractorDocument } from 'src/app/models/contractor-document';
import { ContractorRegistrationDocument } from 'src/app/models/contractor-registration-document';
import { RegistrationStatus } from 'src/app/models/registration';

import { ContractorService, DataEntityFactory } from 'src/app/services';
import { Utilities } from 'src/app/services/utilities';
import { ContractorCustomFieldListComponent } from 'src/app/components/contractors/contractor-custom-field-list/contractor-custom-field-list.component';

@Component({
  selector: 'app-my-contractor-registration-details',
  standalone: false,
  templateUrl: './my-contractor-registration-details.component.html',
  styleUrls: ['./my-contractor-registration-details.component.scss']
})
export class MyContractorRegistrationDetailsComponent implements OnInit {
  registrationId = '';
  contractorId = '';

  registration: ContractorRegistration;
  contractor: Contractor;

  RegistrationStatus = RegistrationStatus;

  form: UntypedFormGroup;
  contractorForm: UntypedFormGroup;
  rcfForm: UntypedFormGroup;

  documents: ContractorRegistrationDocument[] = [];
  contractorDocuments: ContractorDocument[] = [];

  contractorRequirementFields: any[] = [];
  registrationRequirementFields: any[] = [];

  isLoading = false;
  savingContractorRequirements = false;
  savingRegistrationRequirements = false;

  apiBase = Utilities.getRootURL();

  @ViewChild('customFields', { static: false })
  customFields: ContractorCustomFieldListComponent;

  @ViewChild('registrationCustomFields', { static: false })
  registrationCustomFields: ContractorCustomFieldListComponent;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private contractorSvc: ContractorService,
    private fb: UntypedFormBuilder,
    private toastrSvc: ToastrService
  ) {}

  ngOnInit(): void {
    this.form = this.fb.group({});
    this.contractorForm = this.fb.group({});
    this.rcfForm = this.fb.group({});

    this.route.params.subscribe(params => {
      this.registrationId = params.registrationId;
      this.loadRegistration().subscribe();
    });
  }

  loadRegistration(): Observable<ContractorRegistration> {
    this.isLoading = true;

    return this.contractorSvc
      .getContractorRegistration(this.registrationId)
      .pipe(
        finalize(() => (this.isLoading = false)),
        map(registration => {
          if (!registration) {
            this.toastrSvc.error(
              'Registration either does not exist or you do not have permission to view.'
            );
            return null;
          }

          this.registration = registration;
          this.documents = registration.documents || [];
          this.contractorId = registration.contractorID;
          this.contractor = registration.contractor;

          this.prepareRequirementFields(registration);
          this.loadContractorDocuments();

          return registration;
        })
      );
  }

  private prepareRequirementFields(registration: ContractorRegistration): void {
    if (!registration.type?.requirements) {
      this.contractorRequirementFields = [];
      this.registrationRequirementFields = [];
      return;
    }

    const cCustomFields = Object.assign(
      {},
      ...(registration.contractor?.customFields || []).map(cf => ({
        [cf.templateCode]: cf.fieldValue
      }))
    );

    const rCustomFields = Object.assign(
      {},
      ...(registration.customFields || []).map(cf => ({
        [cf.templateCode]: cf.fieldValue
      }))
    );

    this.contractorRequirementFields = registration.type.requirements
      .filter(r => r.isContractorRequirement)
      .map(r => {
        const dataEntityDef =
          r.dataEntityDefinition ||
          DataEntityFactory.createDataEntity('free-form-text', {
            templateCode: r.templateCode,
            label: r.label
          });

        return {
          label: r.label,
          templateCode: r.templateCode,
          dataEntityDef,
          dataEntityTypeCode: dataEntityDef
            ? dataEntityDef.dataEntityTypeCode
            : 'free-form-text',
          fieldValue: cCustomFields[r.templateCode],
          contractorId: registration.contractorID,
          dataEntityType: dataEntityDef
            ? dataEntityDef.dataEntityTypeCode
            : 'free-form-text'
        };
      });

    this.registrationRequirementFields = registration.type.requirements
      .filter(r => !r.isContractorRequirement)
      .map(r => {
        const dataEntityDef =
          r.dataEntityDefinition ||
          DataEntityFactory.createDataEntity('free-form-text', {
            templateCode: r.templateCode,
            label: r.label
          });

        return {
          label: r.label,
          templateCode: r.templateCode,
          dataEntityDef,
          dataEntityTypeCode: dataEntityDef
            ? dataEntityDef.dataEntityTypeCode
            : 'free-form-text',
          fieldValue: rCustomFields[r.templateCode],
          registrationId: registration.id,
          dataEntityType: dataEntityDef
            ? dataEntityDef.dataEntityTypeCode
            : 'free-form-text'
        };
      });
  }

  loadContractorDocuments(): void {
    if (!this.contractorId) {
      this.contractorDocuments = [];
      return;
    }

    this.contractorSvc
      .getContractorDocuments(this.contractorId)
      .subscribe(docs => {
        this.contractorDocuments = docs || [];
      });
  }

  loadDocuments(): void {
    this.loadContractorDocuments();

    this.contractorSvc
      .getContractorRegistrationDocuments(this.registrationId)
      .subscribe(docs => {
        this.documents = docs || [];
      });
  }

  addNewDocuments(documents: Document[]): void {
    const regDocs = documents.map(document => {
      const doc = new ContractorRegistrationDocument();
      doc.documentName = document.name;
      doc.documentPath = document.path;
      return doc;
    });

    this.contractorSvc
      .saveContractorRegistrationDocuments(this.registrationId, regDocs)
      .subscribe(docs => {
        this.documents = docs || [];
        this.toastrSvc.success('Document(s) Saved');
      });
  }

  addNewContractorDocuments(documents: Document[]): void {
    const contractorDocs = documents.map(document => {
      const doc = new ContractorDocument();
      doc.documentName = document.name;
      doc.documentPath = document.path;
      return doc;
    });

    this.contractorSvc
      .saveContractorDocuments(this.contractorId, contractorDocs)
      .subscribe(() => {
        this.loadDocuments();
        this.toastrSvc.success('Document(s) Saved');
      });
  }

  deleteDocument(documentId: string): void {
    this.contractorSvc
      .deleteContractorRegistrationDocument(documentId)
      .subscribe(() => {
        this.toastrSvc.success('Document Deleted');
        this.loadDocuments();
      });
  }

  deleteContractorDocument(documentId: string): void {
    this.contractorSvc.deleteContractorDocument(documentId).subscribe(() => {
      this.toastrSvc.success('Document Deleted');
      this.loadDocuments();
    });
  }

  saveDocument(document: ContractorRegistrationDocument): void {
    this.contractorSvc
      .saveContractorRegistrationDocument(document)
      .subscribe(() => {
        this.toastrSvc.success('Document Saved');
      });
  }

  saveContractorDocument(document: ContractorDocument): void {
    this.contractorSvc.saveContractorDocument(document).subscribe(() => {
      this.toastrSvc.success('Document Saved');
    });
  }

  saveContractorDetails(): void {
    if (!this.contractor || !this.customFields) {
      return;
    }

    this.contractor.customFields = this.customFields.persistValues();
    this.contractor.registrations = [];
    this.contractor.documents = [];
    this.contractor.user = null;

    this.savingContractorRequirements = true;

    this.contractorSvc.saveContractorDetails(this.contractor).subscribe({
      next: () => {
        this.loadRegistration().subscribe(() => {
          this.toastrSvc.success('Contractor Saved');
          this.savingContractorRequirements = false;
        });
      },
      error: () => {
        this.savingContractorRequirements = false;
        this.toastrSvc.error(
          'Your changes were not saved.',
          'Something went wrong!'
        );
      }
    });
  }

  saveRegistrationCustomFields(): void {
    if (!this.registration || !this.registrationCustomFields) {
      return;
    }

    this.registration.customFields = this.registrationCustomFields.persistValues();

    this.savingRegistrationRequirements = true;

    this.contractorSvc
      .saveContractorRegistrationCustomFields(
        this.registration.customFields,
        this.contractorId
      )
      .subscribe({
        next: () => {
          this.loadRegistration().subscribe(() => {
            this.toastrSvc.success('Registration Saved');
            this.savingRegistrationRequirements = false;
          });
        },
        error: () => {
          this.savingRegistrationRequirements = false;
          this.toastrSvc.error(
            'Your changes were not saved.',
            'Something went wrong!'
          );
        }
      });
  }

  getAzureDocumentPath(doc: ContractorDocument): string {
    return Utilities.getAzureUrl(doc);
  }

  newRegistration(): void {
    this.router.navigate(['../../new-registration'], {
      relativeTo: this.route,
      queryParamsHandling: 'merge'
    });
  }

  closeOverlay(): void {
    this.router.navigate(['../../'], {
      relativeTo: this.route,
      queryParamsHandling: 'merge'
    });
  }
}
